import * as React from "react";
import { ReactElement } from "react";
import { Helmet } from "react-helmet";
import { useStaticQuery, graphql } from "gatsby";
import fontUrl1 from "../fonts/gg-regular.woff2";
import fontUrl2 from "../fonts/gg-medium.woff2";
import { DeepNonNullable } from "ts-essentials";
import { GatsbySeo, JsonLd } from "gatsby-plugin-next-seo";
import { useLocation } from "@reach/router";

/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.com/docs/use-static-query/
 */
type SeoProps = {
  title: string;
  locale: string;
  canonical?: string;
  featuredImageURL?: string;
  noindex?: boolean;
  children?: ReactElement | null;
  metaDescription?: string;
  metaKeywords?: string;
};

const Seo = ({
  title,
  locale = "en",
  canonical,
  featuredImageURL = "/featured-image.png",
  noindex = false,
  children = null,
  metaDescription,
  metaKeywords,
}: SeoProps) => {
  const { pathname } = useLocation();
  const { site } = useStaticQuery<DeepNonNullable<Queries.SEODataQuery>>(
    graphql`
      query SEOData {
        site {
          siteMetadata {
            title
            description
            author
            siteUrl
          }
        }
      }
    `
  );

  const description = metaDescription || site.siteMetadata.description;
  const keywords = metaKeywords || undefined;
  const defaultTitle = site.siteMetadata?.title;
  const { siteUrl } = site.siteMetadata;

  const canonicalValue =
    `${canonical || pathname}` === "/"
      ? siteUrl
      : `${siteUrl}${canonical || pathname}`;

  return (
    <>
      <GatsbySeo
        // featuredImageURL
        language={locale}
        title={(title || defaultTitle) + " — AccuRanker"}
        metaTags={[
          {
            name: "keywords",
            content: keywords,
          },
        ]}
        canonical={canonicalValue}
        titleTemplate={"%s"}
        description={description}
        noindex={noindex}
        twitter={{
          cardType: "summary_large_image",
          site: "@accuranker",
          handle: "@accuranker",
        }}
        facebook={{
          appId: "1441162386095703",
        }}
        openGraph={{
          type: "website",
          title: title,
          description: description,
          images: [
            {
              url: featuredImageURL,
            },
          ],
        }}
      />
      <Helmet>
        <link
          rel="preload"
          as="font"
          crossOrigin="anonymous"
          type="font/woff2"
          href={fontUrl1}
        />
        <link
          rel="preload"
          as="font"
          crossOrigin="anonymous"
          type="font/woff2"
          href={fontUrl2}
        />
        <link rel="icon" href="/icons/icon-48x48.png" type="image/png" />
        <link rel="icon" href="/accuranker-icon.svg" type="image/svg+xml" />
        {children}
      </Helmet>

      <JsonLd
        json={{
          "@context": "https://schema.org",
          "@type": "Corporation",
          "@id": "https://www.accuranker.com/#organization",
          name: "AccuRanker",
          legalName: "AccuRanker ApS",
          foundingDate: "2013-01-01",
          description: "AccuRanker is the world's fastest rank tracker",
          logo: "https://www.accuranker.com/icons/icon-256x256.png",
          image: "https://www.accuranker.com/icons/icon-256x256.png",
          url: siteUrl,
          vatID: "DK32932215",
          telephone: "+45 89 87 39 44",
          email: "support@accuranker.com",
          contactPoint: [
            {
              "@type": "ContactPoint",
              contactType: "Customer support",
              telephone: "+45 89 87 39 44",
              email: "hello@accuranker.com",
            },
            {
              "@type": "ContactPoint",
              contactType: "Customer support",
              telephone: "+1 888-428-4948",
              email: "hello@accuranker.com",
            },
            {
              "@type": "ContactPoint",
              contactType: "Customer support",
              telephone: "+44 20 3318 0302",
              email: "hello@accuranker.com",
            },
          ],
          address: [
            {
              "@type": "PostalAddress",
              addressLocality: "Aarhus",
              addressRegion: "Midtjylland",
              addressCountry: "DK",
              postalCode: "8000",
              streetAddress: "Åboulevarden 22, 5",
            },
            {
              "@type": "PostalAddress",
              addressLocality: "Plainview",
              addressCountry: "US",
              postalCode: "NY 11803",
              streetAddress: "329 South Oyster Bay Road #2057",
            },
          ],
          founder: [
            {
              "@type": "Person",
              givenName: "Henrik",
              familyName: "Jørgensen",
            },
          ],
          sameAs: [
            "https://www.facebook.com/AccuRanker",
            "https://www.instagram.com/accuranker/",
            "https://www.linkedin.com/company/accuranker/",
            "https://www.youtube.com/channel/UCv8uTVRcwNF1JkZ1Z0d7WUg",
            "https://twitter.com/accuranker",
          ],
        }}
      />

      <JsonLd
        json={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "AccuRanker",
          url: siteUrl,
        }}
      />

      <JsonLd
        json={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: title,
          description: description,
        }}
      />
    </>
  );
};

export default Seo;
